import { useSortable, SortableTh } from "@/components/common/SortableTable";
/**
 * Universal Import Hub — admin-only page for uploading monthly CSV/XLSX files
 * across every module that doesn't yet have a live API integration.
 *
 * Pattern proven with the P&L Actuals + Budget importers.
 *
 * Flow:
 *   1. Catalogue of recipes (Employees, Licences, Vehicles, ...)
 *   2. Click a recipe → detail sheet with column spec + download template
 *   3. Upload file → server-side preview (validation, insert vs update counts)
 *   4. Confirm apply → import runs, audit trail persisted
 *   5. History table shows every past run per recipe
 */
import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Upload, FileDown, RefreshCw, ShieldAlert, CheckCircle2, XCircle, AlertTriangle,
  Users, Award, ShieldCheck, Truck, HardHat, Leaf, FileText, Loader2, ArrowRight,
  History, Filter, Table as TableIcon, KeyRound, Copy, Trash2, RotateCw, Plug, Activity,
  FileSearch,
} from "lucide-react";
import { Mail } from "lucide-react";

const EMAIL_ROUTES = [
  {
    id: "ampol_fuel_email",
    title: "Ampol Fuel Report",
    desc: "Email the monthly Ampol CSV/XLSX — report month is auto-detected from the filename (YYYY-MM), else defaults to last month. Rolls Scope 1 into emissions.",
    endpoint: "POST /api/sustainability/ampol/import-service",
    subject: "GBE Hub — Ampol Fuel Report 2026-07",
  },
  {
    id: "utility_bills_email",
    title: "Utility Bills — electricity · water · waste · gas",
    desc: "Email bill PDFs — AI extracts retailer, site, period, usage and cost into the Bills Inbox for review, then recomputes the month.",
    endpoint: "POST /api/sustainability/bills/email",
    subject: "GBE Hub — Electricity Bill <site>",
  },
  {
    id: "__aroflo_email",
    title: "AroFlo report emails",
    desc: "Daily JSA · Safety Audit · CCEW · Vehicle Service · Action Items · Client Feedback — routed automatically by subject keywords. Accepts an AroFlo download link in the body OR a CSV/XLSX attachment (base64 contentBytes).",
    endpoint: "POST /api/imports/aroflo-report-email",
    subject: "GBE Hub — Safety Audit Report Aug 2026",
  },
  {
    id: "__feedback_email",
    title: "Client feedback emails (AI-classified)",
    desc: "Forward any client email (thanks, complaints, queries) — AI classifies the type and severity, matches the client and BU, and files it in the Feedback Register, feeding the Customer Service KPIs. Body: {subject, from_email, from_name, body}. High-severity complaints instantly alert the BU manager.",
    endpoint: "POST /api/feedback/email-intake",
    subject: "FW: <any client email>",
  },
];

function EmailRoutesCard({ runs }) {
  const sorted = [...runs].sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));
  const lastFor = (id) => id === "__aroflo_email"
    ? sorted.find((r) => r.source === "power_automate_email" && !["ampol_fuel_email", "utility_bills_email"].includes(r.recipe_id))
    : sorted.find((r) => r.recipe_id === id);
  return (
    <div className="mb-5" data-testid="email-routes-card">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-slate-600" />
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Email-in routes (Power Automate)</div>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {EMAIL_ROUTES.map((rt) => {
          const last = lastFor(rt.id);
          return (
            <div key={rt.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`email-route-${rt.id}`}>
              <div className="text-sm font-medium text-slate-900">{rt.title}</div>
              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{rt.desc}</p>
              <div className="mt-2 text-[10px] font-mono bg-slate-50 border border-slate-100 rounded px-2 py-1 text-slate-600 break-all">{rt.endpoint}</div>
              <div className="mt-1 text-[10px] text-slate-400">Subject: <span className="text-slate-500">{rt.subject}</span></div>
              <div className="mt-2 text-[11px]">
                {last ? (
                  <span className="text-emerald-700">Last received: {(last.uploaded_at || "").slice(0, 16).replace("T", " ")} · {last.filename || last.recipe_title} · {last.rows_total ?? 0} rows</span>
                ) : (
                  <span className="text-slate-400">Nothing received yet</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400 mt-1.5">All routes authenticate with an <span className="font-mono">X-GBE-Service-Key</span> header — create one under the Service Keys tab.</p>
    </div>
  );
}
import { toast } from "sonner";

const MODULE_ICON = {
  hr: Users,
  fleet: Truck,
  capital: HardHat,
  sustainability: Leaf,
  contracts: FileText,
  hseq: ShieldCheck,
};
const MODULE_BADGE = {
  hr: "bg-sky-100 text-sky-700 border-sky-200",
  fleet: "bg-amber-100 text-amber-700 border-amber-200",
  capital: "bg-violet-100 text-violet-700 border-violet-200",
  sustainability: "bg-emerald-100 text-emerald-700 border-emerald-200",
  contracts: "bg-slate-100 text-slate-700 border-slate-200",
  hseq: "bg-rose-100 text-rose-700 border-rose-200",
};

const fmtNum = (v) => new Intl.NumberFormat("en-AU").format(v || 0);
const fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso.slice(0, 16).replace("T", " "); }
};

const fmtCell = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};


// Large uploads are split client-side to stay under gateway body/time limits.
// Splitting respects quoted CSV fields (embedded newlines in notes are safe).
const CHUNK_BYTES = 2 * 1024 * 1024;
const CHUNK_ROWS = 4000;

async function splitCsvFile(file, maxRows) {
  const text = await file.text();
  const rows = [];
  let start = 0, inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') inQ = !inQ;
    else if (c === "\n" && !inQ) { rows.push(text.slice(start, i + 1)); start = i + 1; }
  }
  if (start < text.length) rows.push(text.slice(start));
  if (rows.length < 2) return [new Blob([text], { type: "text/csv" })];
  const header = rows[0].endsWith("\n") ? rows[0] : rows[0] + "\n";
  const body = rows.slice(1);
  const parts = [];
  for (let i = 0; i < body.length; i += maxRows) {
    parts.push(new Blob([header + body.slice(i, i + maxRows).join("")], { type: "text/csv" }));
  }
  return parts;
}

export default function ImportHubPage() {
  const { can, loaded, role } = usePermissions();
  const [recipes, setRecipes] = useState([]);
  const [unresolvedBU, setUnresolvedBU] = useState({ total: 0, feeds: [] });
  const [runs, setRuns] = useState([]);
  const { sorted: sortedRuns, sort: runSort, setSort: setRunSort } = useSortable(runs);
  const [busy, setBusy] = useState(false);
  const [openRecipe, setOpenRecipe] = useState(null);        // recipe_id
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [chunks, setChunks] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [tab, setTab] = useState("catalogue");
  const [runDetail, setRunDetail] = useState(null);
  const [keys, setKeys] = useState([]);
  const [newKeyDlg, setNewKeyDlg] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [issuedSecret, setIssuedSecret] = useState(null);
  const [creatingKey, setCreatingKey] = useState(false);
  const [feedHealth, setFeedHealth] = useState(null);
  // --- Run row viewer (actual inserted/updated rows with column data) ---
  const [runRows, setRunRows] = useState(null);     // {rows, total, counts, skip, limit}
  const [rowOp, setRowOp] = useState("");           // "" | inserted | updated
  const [rowQ, setRowQ] = useState("");
  const [rowPage, setRowPage] = useState(0);
  const [rowsLoading, setRowsLoading] = useState(false);
  const ROW_PAGE = 50;

  const rowColumns = useMemo(() => {
    if (!runRows?.rows?.length) return [];
    const seen = [];
    for (const r of runRows.rows) {
      for (const k of Object.keys(r.doc || {})) {
        if (!seen.includes(k)) seen.push(k);
      }
    }
    return seen;
  }, [runRows]);

  const isAdmin = loaded && (can("org_mgmt", "admin") || can("org_mgmt", "edit") || role === "finance_admin");

  const load = async () => {
    setBusy(true);
    try {
      const [r, h, k, fh] = await Promise.all([
        api.get("/imports/recipes"),
        api.get("/imports/history?limit=50"),
        api.get("/imports/service-keys").catch(() => ({ data: { keys: [] } })),
        api.get("/imports/feed-health").catch(() => ({ data: null })),
      ]);
      setRecipes(r.data.recipes || []);
      setRuns(h.data.runs || []);
      setKeys(k.data.keys || []);
      setFeedHealth(fh.data);
      api.get("/imports/unresolved-business-units")
        .then((u) => setUnresolvedBU(u.data || { total: 0, feeds: [] }))
        .catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Import Hub");
    } finally { setBusy(false); }
  };
  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [loaded]);

  const openDetail = async (rid) => {
    setOpenRecipe(rid);
    setPreview(null); setPreviewFile(null); setDetail(null);
    try {
      const { data } = await api.get(`/imports/recipes/${rid}`);
      setDetail(data);
    } catch (e) { toast.error("Failed to load recipe detail"); }
  };

  const [resyncing, setResyncing] = useState(false);
  const resyncBUs = async (rid) => {
    setResyncing(true);
    try {
      const { data } = await api.post(`/imports/recipes/${rid}/resync-business-units`);
      if (data.fixed > 0) {
        const who = (data.retagged || []).map((x) => `${x.who} → ${x.bu}`).slice(0, 6).join(", ");
        toast.success(`Re-tagged ${data.fixed} employee${data.fixed === 1 ? "" : "s"}${who ? `: ${who}` : ""}`);
      } else if (data.scanned === 0) {
        toast.info("No employees with an unresolved Business Unit — nothing to re-sync.");
      } else {
        toast.warning(`Scanned ${data.scanned}, but none could be resolved (codes: ${(data.still_unresolved || []).join(", ") || "—"}).`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-sync failed");
    } finally { setResyncing(false); }
  };

  const resyncAllBUs = async () => {
    setResyncing(true);
    try {
      const { data } = await api.post(`/imports/resync-business-units-all`);
      if (data.total_fixed > 0) {
        const feeds = (data.feeds || []).filter((f) => f.fixed > 0)
          .map((f) => `${f.title} (${f.fixed})`).join(", ");
        toast.success(`Re-tagged ${data.total_fixed} record${data.total_fixed === 1 ? "" : "s"}${feeds ? ` across ${feeds}` : ""}`);
      } else {
        toast.info("Nothing to re-sync — no feed has an unresolved Business Unit.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-sync failed");
    } finally { setResyncing(false); }
  };

  const downloadTemplate = async (rid) => {
    try {
      const r = await api.get(`/imports/recipes/${rid}/template.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${rid}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { toast.error("Template download failed"); }
  };

  const handlePreview = async (file) => {
    if (!file) return;
    setPreviewFile(file);
    setChunks(null);
    setUploading(true);
    try {
      let uploadPart = file;
      if (file.size > CHUNK_BYTES && /\.csv$/i.test(file.name)) {
        const parts = await splitCsvFile(file, CHUNK_ROWS);
        if (parts.length > 1) {
          setChunks(parts);
          uploadPart = new File([parts[0]], file.name, { type: "text/csv" });
          toast.info(`Large file — will upload in ${parts.length} parts to avoid gateway limits. Preview shows part 1 of ${parts.length}.`);
        }
      }
      const form = new FormData();
      form.append("file", uploadPart);
      const { data } = await api.post(
        `/imports/recipes/${openRecipe}/preview`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setUploading(false); }
  };

  const handleApply = async () => {
    if (!previewFile) return;
    const isChunked = chunks && chunks.length > 1;
    const msg = isChunked
      ? `Apply large file to '${detail?.title}' in ${chunks.length} parts?\n\n(Preview stats were for part 1 only — all ${chunks.length} parts will be applied. Upserts make this safe to re-run.)`
      : `Apply ${preview?.rows_valid ?? 0} valid rows to '${detail?.title}'?\n\nInserts: ${preview?.would_insert}\nUpdates: ${preview?.would_update}\nErrored (skipped): ${preview?.rows_errored}`;
    if (!confirm(msg)) return;
    setApplying(true);
    let part = 0;
    try {
      if (isChunked) {
        let ins = 0, upd = 0, err = 0;
        for (let i = 0; i < chunks.length; i++) {
          part = i + 1;
          setApplyProgress(`${part}/${chunks.length}`);
          const form = new FormData();
          form.append("file", new File([chunks[i]], previewFile.name, { type: "text/csv" }));
          const { data } = await api.post(
            `/imports/recipes/${openRecipe}/apply`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          ins += data.rows_inserted || 0; upd += data.rows_updated || 0; err += data.rows_errored || 0;
        }
        toast.success(`Applied all ${chunks.length} parts: ${ins} new · ${upd} updated · ${err} errored`);
      } else {
        const form = new FormData();
        form.append("file", previewFile);
        const { data } = await api.post(
          `/imports/recipes/${openRecipe}/apply`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        toast.success(`Applied: ${data.rows_inserted} new · ${data.rows_updated} updated · ${data.rows_errored} errored`);
      }
      setPreview(null); setPreviewFile(null); setChunks(null);
      setOpenRecipe(null);
      await load();
    } catch (e) {
      const detail_msg = e.response?.data?.detail || "Apply failed";
      toast.error(isChunked ? `${detail_msg} (failed at part ${part}/${chunks.length} — already-applied parts are safe, just re-run)` : detail_msg);
    } finally { setApplying(false); setApplyProgress(null); }
  };

  const openRun = async (id) => {
    setRunRows(null); setRowOp(""); setRowQ(""); setRowPage(0);
    try {
      const { data } = await api.get(`/imports/history/${id}`);
      setRunDetail(data);
      fetchRunRows(id, { op: "", q: "", page: 0 });
    } catch (e) { toast.error("Failed to load run detail"); }
  };

  const fetchRunRows = async (runId, { op, q, page }) => {
    setRowsLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(ROW_PAGE), skip: String(page * ROW_PAGE) });
      if (op) params.set("op", op);
      if (q && q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/imports/history/${runId}/rows?${params.toString()}`);
      setRunRows(data);
    } catch (e) {
      setRunRows({ rows: [], total: 0, counts: { inserted: 0, updated: 0 } });
    } finally { setRowsLoading(false); }
  };

  // ---- Service keys ----
  const createKey = async () => {
    if (!newKeyName.trim()) { toast.error("Give the key a name (e.g. 'Power Automate — HR')"); return; }
    setCreatingKey(true);
    try {
      const { data } = await api.post("/imports/service-keys", {
        name: newKeyName.trim(),
        allowed_recipes: ["*"],   // all recipes per user's decision
      });
      setIssuedSecret(data);
      setNewKeyName("");
      setNewKeyDlg(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create key");
    } finally { setCreatingKey(false); }
  };
  const renameKey = async (id, currentName) => {
    const name = window.prompt("Rename this key", currentName || "")?.trim();
    if (!name || name === currentName) return;
    try {
      await api.patch(`/imports/service-keys/${id}`, { name });
      toast.success("Key renamed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
  };

  const rotateKey = async (id) => {
    if (!confirm("Rotate this key? The current secret will stop working immediately.")) return;
    try {
      const { data } = await api.post(`/imports/service-keys/${id}/rotate`);
      setIssuedSecret(data);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rotate failed"); }
  };
  const revokeKey = async (id) => {
    if (!confirm("Revoke this key? This cannot be undone (create a fresh one afterwards if needed).")) return;
    try {
      await api.delete(`/imports/service-keys/${id}`);
      toast.success("Key revoked");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const byModule = useMemo(() => {
    const g = {};
    recipes.forEach((r) => {
      g[r.module] = g[r.module] || [];
      g[r.module].push(r);
    });
    return g;
  }, [recipes]);

  if (loaded && !isAdmin) {
    return (
      <AppLayout title="Import Hub">
        <div className="max-w-md mx-auto mt-16 text-center">
          <ShieldAlert className="w-6 h-6 text-rose-700 inline-block" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">Admins only</h2>
          <p className="text-sm text-slate-500 mt-1">The Import Hub is restricted to executive / admin roles.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Import Hub"
      subtitle="Universal CSV / XLSX importers for every module — used monthly by an ops admin"
      actions={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="import-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      }
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto">
          <TabsTrigger data-testid="import-tab-catalogue" value="catalogue" className="text-xs data-[state=active]:bg-white">
            <TableIcon className="w-3.5 h-3.5 mr-1.5" /> Catalogue ({recipes.length})
          </TabsTrigger>
          <TabsTrigger data-testid="import-tab-feed-health" value="health" className="text-xs data-[state=active]:bg-white">
            <Activity className="w-3.5 h-3.5 mr-1.5" /> Feed Health
            {(feedHealth?.summary?.overdue || 0) > 0 && (
              <span className="ml-1.5 px-1.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold">
                {feedHealth.summary.overdue}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger data-testid="import-tab-history" value="history" className="text-xs data-[state=active]:bg-white">
            <History className="w-3.5 h-3.5 mr-1.5" /> History ({runs.length})
          </TabsTrigger>
          <TabsTrigger data-testid="import-tab-service-keys" value="keys" className="text-xs data-[state=active]:bg-white">
            <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Service Keys ({keys.filter(k => k.enabled).length})
          </TabsTrigger>
          <TabsTrigger data-testid="import-tab-doc-check" value="doc-check" className="text-xs data-[state=active]:bg-white">
            <FileSearch className="w-3.5 h-3.5 mr-1.5" /> Doc check
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalogue" className="mt-4">
          <div className="mb-3 text-xs text-slate-500">
            Each card is a monthly-import target. Click <span className="font-medium">Configure &amp; upload</span> to see the column spec, download a template, then preview and apply the file.
          </div>
          {unresolvedBU.total > 0 && (
            <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
                 data-testid="unresolved-bu-banner">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-900">
                  {unresolvedBU.total} record{unresolvedBU.total === 1 ? "" : "s"} across {unresolvedBU.feeds.length} feed{unresolvedBU.feeds.length === 1 ? "" : "s"} have an unresolved Business Unit
                </div>
                <div className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                  Their import sent a BU code that didn't match a unit, so they're excluded from their parent's headcount/KPIs.{" "}
                  {unresolvedBU.feeds.map((f, i) => (
                    <span key={f.recipe} className="whitespace-nowrap">
                      {f.title} ({f.count}){i < unresolvedBU.feeds.length - 1 ? " · " : ""}
                    </span>
                  ))}
                  . Click <span className="font-medium">Re-sync all BUs</span> to fix them in one go.
                </div>
              </div>
              <Button size="sm" onClick={resyncAllBUs} disabled={resyncing}
                      data-testid="unresolved-bu-resync-btn"
                      className="text-xs h-8 bg-amber-600 hover:bg-amber-700 text-white shrink-0">
                <RefreshCw className={`w-3 h-3 mr-1.5 ${resyncing ? "animate-spin" : ""}`} /> Re-sync all BUs
              </Button>
            </div>
          )}
          <EmailRoutesCard runs={runs} />
          {Object.keys(byModule).sort().map((mod) => {
            const Icon = MODULE_ICON[mod] || FileText;
            const badge = MODULE_BADGE[mod] || "bg-slate-100 text-slate-700 border-slate-200";
            return (
              <div key={mod} className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-slate-600" />
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{mod}</div>
                  <span className="text-[10px] text-slate-400">· {byModule[mod].length} recipe{byModule[mod].length === 1 ? "" : "s"}</span>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {byModule[mod].map((r) => (
                    <div key={r.recipe_id}
                         data-testid={`import-recipe-card-${r.recipe_id}`}
                         className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col justify-between hover:border-slate-300 transition">
                      <div>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="font-display text-base font-semibold text-slate-900">{r.title}</div>
                          <Badge className={`text-[9px] border ${badge}`}>{r.module}</Badge>
                        </div>
                        <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-3">{r.description}</p>
                        <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
                          <div>
                            <div className="text-slate-400 uppercase tracking-wider">Rows</div>
                            <div className="text-slate-800 font-medium tabular-nums">{fmtNum(r.row_count)}</div>
                          </div>
                          <div>
                            <div className="text-slate-400 uppercase tracking-wider">Cols</div>
                            <div className="text-slate-800 font-medium tabular-nums">{r.column_count}</div>
                          </div>
                          <div>
                            <div className="text-slate-400 uppercase tracking-wider">Key</div>
                            <div className="text-slate-800 font-medium truncate" title={r.unique_key.join(", ")}>{r.unique_key.join(", ")}</div>
                          </div>
                        </div>
                        <div className="mt-2 text-[10px] text-slate-400">
                          Last import: {r.last_import_at ? `${fmtWhen(r.last_import_at)} by ${r.last_import_by || "—"}` : "Never"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                        {r.recipe_id === "employees" && (
                          <Button size="sm" variant="outline" onClick={() => resyncBUs(r.recipe_id)}
                                  disabled={resyncing}
                                  title="Re-tag employees whose Business Unit code (e.g. 610 HVAC / 430 Solar) failed to resolve on import"
                                  data-testid="import-resync-bu-btn"
                                  className="text-xs h-8">
                            <RefreshCw className={`w-3 h-3 mr-1.5 ${resyncing ? "animate-spin" : ""}`} /> Re-sync BUs
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => downloadTemplate(r.recipe_id)}
                                data-testid={`import-template-btn-${r.recipe_id}`}
                                className="text-xs h-8">
                          <FileDown className="w-3 h-3 mr-1.5" /> Template
                        </Button>
                        <Button size="sm" onClick={() => openDetail(r.recipe_id)}
                                data-testid={`import-open-btn-${r.recipe_id}`}
                                className="text-xs h-8 bg-gbe hover:bg-gbe/90 text-white ml-auto">
                          <Upload className="w-3 h-3 mr-1.5" /> Configure &amp; upload
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <EmployeeIdDedupeCard />
          <EmployeeDedupeCard />
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <SortableTh label="When" k="uploaded_at" sort={runSort} setSort={setRunSort} />
                  <SortableTh label="Recipe" k="recipe_title" sort={runSort} setSort={setRunSort} />
                  <SortableTh label="By" k="uploaded_by_email" sort={runSort} setSort={setRunSort} />
                  <SortableTh label="File" k="filename" sort={runSort} setSort={setRunSort} />
                  <SortableTh label="Total" k="total_rows" sort={runSort} setSort={setRunSort} className="text-right px-3 py-2 font-medium" />
                  <SortableTh label="Insert" k="inserted" sort={runSort} setSort={setRunSort} className="text-right px-3 py-2 font-medium" />
                  <SortableTh label="Update" k="updated" sort={runSort} setSort={setRunSort} className="text-right px-3 py-2 font-medium" />
                  <SortableTh label="Errored" k="errored" sort={runSort} setSort={setRunSort} className="text-right px-3 py-2 font-medium" />
                  <th className="text-right font-medium px-3 py-2">Duration</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sortedRuns.map((r) => (
                  <tr key={r.id} data-testid={`import-run-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 text-slate-700">{fmtWhen(r.uploaded_at)}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{r.recipe_title}</td>
                    <td className="px-3 py-2 text-slate-600">{r.uploaded_by_email}</td>
                    <td className="px-3 py-2 text-slate-500 max-w-[240px] truncate" title={r.filename}>{r.filename}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.rows_total)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtNum(r.rows_inserted)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-sky-700">{fmtNum(r.rows_updated)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.rows_errored > 0 ? "text-rose-700" : "text-slate-400"}`}>{fmtNum(r.rows_errored)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtNum(r.duration_ms)} ms</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => openRun(r.id)}
                              data-testid={`import-run-detail-${r.id}`}>Detail</Button>
                    </td>
                  </tr>
                ))}
                {runs.length === 0 && (
                  <tr><td colSpan="10" className="px-3 py-8 text-center text-slate-500">No imports yet — pick a recipe from the Catalogue to run your first import.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* --- Compliance document-name diagnostic tab --- */}
        <TabsContent value="doc-check" className="mt-4">
          <ComplianceRawDiagnosticCard active={tab === "doc-check"} />
        </TabsContent>

        {/* --- Service Keys tab --- */}
        <TabsContent value="keys" className="mt-4">
          <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <Plug className="w-4 h-4" /> Machine-to-machine ingest
              </div>
              <div className="text-[11px] text-slate-500 mt-1 max-w-2xl leading-relaxed">
                Service keys let Power Automate flows (or any HTTP client) POST JSON rows directly to any Import Hub recipe.
                Each key is shown ONCE at creation — copy it immediately.
                See <span className="font-mono text-[10px] bg-slate-100 px-1 rounded">POWER_AUTOMATE_IMPORTS.md</span> for a step-by-step SharePoint list → Import Hub flow.
              </div>
            </div>
            <Button size="sm" onClick={() => setNewKeyDlg(true)}
                    data-testid="import-create-key-btn"
                    className="bg-gbe hover:bg-gbe/90 text-white">
              <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Create key
            </Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-medium px-3 py-2">Key ID</th>
                  <th className="text-left font-medium px-3 py-2">Name</th>
                  <th className="text-left font-medium px-3 py-2">Scope</th>
                  <th className="text-left font-medium px-3 py-2">Created</th>
                  <th className="text-left font-medium px-3 py-2">Last used</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.key_id} data-testid={`import-key-row-${k.key_id}`}
                      className={`border-b border-slate-100 hover:bg-slate-50/60 ${!k.enabled ? "opacity-60" : ""}`}>
                    <td className="px-3 py-2 font-mono text-slate-800">{k.key_id}</td>
                    <td className="px-3 py-2 text-slate-800">{k.name}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {(k.allowed_recipes || ["*"]).includes("*")
                        ? <Badge className="text-[9px] bg-slate-100 text-slate-700 border border-slate-200">All recipes</Badge>
                        : (k.allowed_recipes || []).map((r) => (
                            <Badge key={r} className="text-[9px] mr-1 bg-sky-100 text-sky-700 border border-sky-200">{r}</Badge>
                          ))}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{fmtWhen(k.created_at)}<div className="text-[9px] text-slate-400">by {k.created_by}</div></td>
                    <td className="px-3 py-2 text-slate-500">{k.last_used_at ? fmtWhen(k.last_used_at) : "Never"}
                      {k.last_used_ip && <div className="text-[9px] text-slate-400 font-mono">{k.last_used_ip}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {k.enabled
                        ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border border-emerald-200">Active</Badge>
                        : <Badge className="text-[9px] bg-slate-100 text-slate-500 border border-slate-200">Revoked</Badge>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {k.enabled && (
                        <>
                          <Button size="sm" variant="outline" className="h-7 text-[10px] mr-1"
                                  data-testid={`import-key-rename-${k.key_id}`}
                                  onClick={() => renameKey(k.key_id, k.name)}>
                            <KeyRound className="w-3 h-3 mr-1" /> Rename
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[10px] mr-1"
                                  data-testid={`import-key-rotate-${k.key_id}`}
                                  onClick={() => rotateKey(k.key_id)}>
                            <RotateCw className="w-3 h-3 mr-1" /> Rotate
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[10px] text-rose-700 border-rose-200 hover:bg-rose-50"
                                  data-testid={`import-key-revoke-${k.key_id}`}
                                  onClick={() => revokeKey(k.key_id)}>
                            <Trash2 className="w-3 h-3 mr-1" /> Revoke
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {keys.length === 0 && (
                  <tr><td colSpan="7" className="px-3 py-8 text-center text-slate-500">No service keys yet — create one to let Power Automate push data into the Hub.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Curl / Power Automate quick reference */}
          <div className="mt-4 bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono leading-relaxed">
            <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-2">Quick reference — Power Automate HTTP action</div>
            <div><span className="text-slate-500">Method:</span> POST</div>
            <div><span className="text-slate-500">URI:</span>    {`{{BACKEND_URL}}`}/api/imports/recipes/<span className="text-emerald-400">&lt;recipe_id&gt;</span>/ingest</div>
            <div><span className="text-slate-500">Headers:</span></div>
            <div className="ml-4">Content-Type: application/json</div>
            <div className="ml-4">X-GBE-Service-Key: <span className="text-emerald-400">iks_xxxxxxxxxxxx.your_secret</span></div>
            <div><span className="text-slate-500">Body:</span> {"{"} "row": {"{"} "Registration": "ABC123", "Make": "Toyota", ... {"}"} {"}"}</div>
          </div>

          {/* Email-body ingest — dedicated closing-stock flow */}
          <div className="mt-4 bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono leading-relaxed"
               data-testid="import-email-quickref">
            <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-2">
              Quick reference — Monthly closing-stock email → Power Automate → Hub
            </div>
            <div className="text-slate-300 font-sans text-[11px] mb-2 leading-snug">
              Purchasing manager emails <span className="text-emerald-400">powerautomate@gbelectrical.com.au</span> with a
              subject like <span className="text-amber-300">"Closing Stock - July 2026"</span> and a table pasted into the body
              (e.g. bare BU codes <code className="text-emerald-400 bg-slate-800 px-1 rounded">200 · 210 · 300 · 400 · 600 · 700 · 710</code>
              in one column, <code className="text-emerald-400 bg-slate-800 px-1 rounded">$ amount</code> in the next).
              A Power Automate flow watches the mailbox and forwards the JSON below.
              A trailing <code className="text-amber-300 bg-slate-800 px-1 rounded">TOTAL</code> row is auto-skipped.
              Service key must be scoped to <code className="text-emerald-400 bg-slate-800 px-1 rounded">closing_stock_email</code>.
            </div>
            <div><span className="text-slate-500">Method:</span> POST</div>
            <div><span className="text-slate-500">URI:</span>    {`{{BACKEND_URL}}`}/api/finance/stock-snapshots/email</div>
            <div><span className="text-slate-500">Headers:</span></div>
            <div className="ml-4">Content-Type: application/json</div>
            <div className="ml-4">X-GBE-Service-Key: <span className="text-emerald-400">iks_xxxxxxxxxxxx.your_secret</span></div>
            <div><span className="text-slate-500">Body (from Power Automate email tokens):</span></div>
            <div className="ml-4 whitespace-pre-wrap text-slate-200">
{`{
  "subject":    "@{triggerOutputs()?['body/subject']}",
  "from":       "@{triggerOutputs()?['body/from']}",
  "html_body":  "@{triggerOutputs()?['body/body']}",
  "plain_body": "@{triggerOutputs()?['body/bodyPreview']}"
}`}
            </div>
            <div className="text-slate-400 text-[10px] mt-3 leading-snug font-sans">
              Response includes <code className="text-emerald-400 bg-slate-800 px-1 rounded">period_month</code>,
              <code className="text-emerald-400 bg-slate-800 px-1 rounded ml-1">inserted</code>,
              <code className="text-emerald-400 bg-slate-800 px-1 rounded ml-1">updated</code>, and any
              <code className="text-emerald-400 bg-slate-800 px-1 rounded ml-1">skipped_bu_codes</code>.
              Table orientation (wide vs long) and BU-vs-JN prefix are auto-detected.
              Period defaults to prior month if not in the subject.
              Each run writes an audit row visible in the <b>History</b> tab.
            </div>
          </div>

          {/* JSA feeds — scheduled AroFlo report exports via file ingest */}
          <div className="mt-4 bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono leading-relaxed"
               data-testid="import-jsa-quickref">
            <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-2">
              Quick reference — JSA feeds (AroFlo scheduled reports → Power Automate → Hub)
            </div>
            <div className="text-slate-300 font-sans text-[11px] mb-2 leading-snug">
              In AroFlo, schedule the <span className="text-amber-300">"Daily JSA Compliance"</span> and
              <span className="text-amber-300"> "Safety Audit Report"</span> exports (XLSX, rolling last 30 days,
              weekly Mon 6am) to the shared mailbox. Two Power Automate flows — trigger
              <span className="text-emerald-400"> "When a new email arrives"</span> with the matching Subject Filter and
              <b> Include Attachments: Yes</b> — then <span className="text-emerald-400">Apply to each attachment</span> with
              the HTTP action below. Files are posted as-is; re-imports upsert (never duplicate).
              Service key must be scoped to <code className="text-emerald-400 bg-slate-800 px-1 rounded">jsa_daily_completion</code> +
              <code className="text-emerald-400 bg-slate-800 px-1 rounded ml-1">jsa_audit_results</code>.
            </div>
            <div><span className="text-slate-500">Method:</span> POST</div>
            <div><span className="text-slate-500">URI (JSA forms):</span>   {`{{BACKEND_URL}}`}/api/imports/recipes/<span className="text-emerald-400">jsa_daily_completion</span>/ingest/file</div>
            <div><span className="text-slate-500">URI (audits):</span>      {`{{BACKEND_URL}}`}/api/imports/recipes/<span className="text-emerald-400">jsa_audit_results</span>/ingest/file</div>
            <div><span className="text-slate-500">Headers:</span></div>
            <div className="ml-4">X-GBE-Service-Key: <span className="text-emerald-400">iks_xxxxxxxxxxxx.your_secret</span></div>
            <div><span className="text-slate-500">Body (raw JSON — multipart file from the email attachment):</span></div>
            <div className="ml-4 whitespace-pre-wrap text-slate-200">
{`{
  "$content-type": "multipart/form-data",
  "$multipart": [
    {
      "headers": {
        "Content-Disposition": "form-data; name=\\"file\\"; filename=\\"@{items('Apply_to_each')?['name']}\\""
      },
      "body": {
        "$content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "$content": "@{items('Apply_to_each')?['contentBytes']}"
      }
    }
  ]
}`}
            </div>
            <div className="text-slate-400 text-[10px] mt-3 leading-snug font-sans">
              Attachment <code className="text-emerald-400 bg-slate-800 px-1 rounded">contentBytes</code> is already base64 — no conversion needed.
              Response: <code className="text-emerald-400 bg-slate-800 px-1 rounded">rows_total / rows_inserted / rows_updated / rows_errored</code>.
              Rows with junk names (e.g. "No Group") count as errored and are harmless.
              Feeds power the JSA columns on <b>HR → Employee Performance</b>.
            </div>
          </div>

          {/* Monthly JSA & CCEW Audit workbook — SharePoint XLSX via email */}
          <div className="mt-4 bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono leading-relaxed"
               data-testid="import-ccew-quickref">
            <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-2">
              Quick reference — Monthly JSA &amp; CCEW Audit workbook (SharePoint → Power Automate → Hub)
            </div>
            <div className="text-slate-300 font-sans text-[11px] mb-2 leading-snug">
              Email the monthly workbook (one <b>.xlsx per BU</b>) to the shared mailbox with a subject like
              <span className="text-amber-300"> "Monthly JSA and CCEW Audit BU200 Sep 2026"</span> — the words
              <span className="text-emerald-400"> "jsa and ccew"</span> (or <span className="text-emerald-400">"ccew"</span>)
              in the subject route it automatically. A Power Automate flow — trigger
              <span className="text-emerald-400"> "When a new email arrives"</span>, <b>Include Attachments: Yes</b>,
              then <span className="text-emerald-400">Apply to each attachment</span> → the HTTP action below.
              The workbook's title row tells the Hub which BU &amp; month it is; re-sends upsert (never duplicate).
              Service key must be scoped to <code className="text-emerald-400 bg-slate-800 px-1 rounded">jsa_ccew_audit</code>.
            </div>
            <div><span className="text-slate-500">Method:</span> POST</div>
            <div><span className="text-slate-500">URI:</span>    {`{{BACKEND_URL}}`}/api/imports/recipes/<span className="text-emerald-400">jsa_ccew_audit</span>/ingest/file</div>
            <div><span className="text-slate-500">Headers:</span></div>
            <div className="ml-4">X-GBE-Service-Key: <span className="text-emerald-400">iks_xxxxxxxxxxxx.your_secret</span></div>
            <div><span className="text-slate-500">Body (raw JSON — multipart file from the email attachment):</span></div>
            <div className="ml-4 whitespace-pre-wrap text-slate-200">
{`{
  "$content-type": "multipart/form-data",
  "$multipart": [
    {
      "headers": {
        "Content-Disposition": "form-data; name=\\"file\\"; filename=\\"@{items('Apply_to_each')?['name']}\\""
      },
      "body": {
        "$content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "$content": "@{items('Apply_to_each')?['contentBytes']}"
      }
    }
  ]
}`}
            </div>
            <div className="text-slate-400 text-[10px] mt-3 leading-snug font-sans">
              Prefer not to automate? Any month can be loaded by hand from the <b>Catalogue</b> above —
              open <span className="text-emerald-400">Monthly JSA &amp; CCEW Audit</span> → drop the .xlsx → preview → apply.
              Keep the original workbook (summary block intact) — a re-saved CSV loses the Audits Required / JSA Completed
              figures and the <b>JSA Audit Completion</b> KPI stays blank.
            </div>
          </div>

          {/* Monthly Completed Tasks report — AroFlo export → audit sampler */}
          <div className="mt-4 bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono leading-relaxed"
               data-testid="import-completed-tasks-quickref">
            <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-2">
              Quick reference — Monthly Completed Tasks Report (AroFlo → Power Automate → Hub)
            </div>
            <div className="text-slate-300 font-sans text-[11px] mb-2 leading-snug">
              AroFlo emails the monthly completed-tasks export (CSV/XLSX, must keep the
              <span className="text-amber-300"> Lab Resource</span> column). A Power Automate flow — trigger
              <span className="text-emerald-400"> "When a new email arrives"</span>, <b>Include Attachments: Yes</b> —
              posts it to the endpoint below. Passing <span className="text-emerald-400">recipe_id</span> explicitly is the
              most reliable route; otherwise the subject must contain <span className="text-emerald-400">"completed tasks"</span>
              (and none of: ccew, jsa, safety audit, incident, employee, contract, vehicle). Service key must be scoped to
              <code className="text-emerald-400 bg-slate-800 px-1 rounded"> completed_tasks</code>. Once a month lands, the
              10% JSA/CCEW audit sample auto-generates. Re-sends upsert (never duplicate).
            </div>
            <div><span className="text-slate-500">Method:</span> POST</div>
            <div><span className="text-slate-500">URI:</span>    {`{{BACKEND_URL}}`}/api/imports/<span className="text-emerald-400">aroflo-report-email</span></div>
            <div><span className="text-slate-500">Headers:</span></div>
            <div className="ml-4">Content-Type: application/json</div>
            <div className="ml-4">X-GBE-Service-Key: <span className="text-emerald-400">iks_xxxxxxxxxxxx.your_secret</span></div>
            <div><span className="text-slate-500">Body (JSON):</span></div>
            <div className="ml-4 whitespace-pre-wrap text-slate-200">
{`{
  "recipe_id": "completed_tasks",
  "subject":   "@{triggerOutputs()?['body/subject']}",
  "html_body": "@{triggerOutputs()?['body/body']}",
  "attachments": @{triggerOutputs()?['body/attachments']}
}`}
            </div>
            <div className="text-slate-400 text-[10px] mt-3 leading-snug font-sans">
              Manual catch-up for a month: <b>Catalogue</b> above → open
              <span className="text-emerald-400"> Completed Tasks (monthly, for JSA/CCEW audit sampling)</span> → drop the CSV →
              preview → apply. Rows with a blank Lab Resource (archived / no labour) are skipped by design.
            </div>
          </div>

          {/* HR feeds — SharePoint lists via scheduled batch ingest */}
          <div className="mt-4 bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono leading-relaxed"
               data-testid="import-hr-quickref">
            <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-2">
              Quick reference — HR feeds (SharePoint → Power Automate → Hub)
            </div>
            <div className="text-slate-300 font-sans text-[11px] mb-2 leading-snug">
              Three scheduled flows, one pattern: <span className="text-emerald-400">Recurrence</span> (e.g. Mon 6:30am)
              → <span className="text-emerald-400">Get items</span> (SharePoint list)
              → <span className="text-emerald-400">Select</span> (map columns as below)
              → <span className="text-emerald-400">HTTP POST</span> the rows.
              Run <b>Employees first</b> the first time — Licences &amp; Certifications link by <b>Employee Email</b>.
              Email is the employee identifier (employee numbers are optional).
              First/Last Name must match the AroFlo spelling on first sync. Re-runs upsert (never duplicate).
              Max <b>500 rows per call</b> — for bigger lists loop in chunks.
              Service key must be scoped to <code className="text-emerald-400 bg-slate-800 px-1 rounded">employees</code>,
              <code className="text-emerald-400 bg-slate-800 px-1 rounded ml-1">hr_licences</code>,
              <code className="text-emerald-400 bg-slate-800 px-1 rounded ml-1">hr_certifications</code>.
            </div>
            <div><span className="text-slate-500">Method:</span> POST · <span className="text-slate-500">Headers:</span> Content-Type: application/json · X-GBE-Service-Key: <span className="text-emerald-400">iks_xxxx.secret</span></div>
            <div className="mt-1"><span className="text-slate-500">URI (employees):</span>      {`{{BACKEND_URL}}`}/api/imports/recipes/<span className="text-emerald-400">employees</span>/ingest/batch</div>
            <div><span className="text-slate-500">URI (licences):</span>       {`{{BACKEND_URL}}`}/api/imports/recipes/<span className="text-emerald-400">hr_licences</span>/ingest/batch</div>
            <div><span className="text-slate-500">URI (certifications):</span> {`{{BACKEND_URL}}`}/api/imports/recipes/<span className="text-emerald-400">hr_certifications</span>/ingest/batch</div>
            <div className="mt-2"><span className="text-slate-500">Body — Employees (map each value from your SharePoint columns):</span></div>
            <div className="ml-4 whitespace-pre-wrap text-slate-200">
{`{
  "rows": [
    {
      "Email": "kathy.abdoo@gbelectrical.com.au",
      "First Name": "Kathy",
      "Last Name": "Abdoo",
      "Phone": "0400123456",
      "Business Unit Code": "JN300",
      "Role Title": "Administration",
      "Employment Type": "GB Employee",
      "Start Date": "2015-02-01",
      "Active": "true"
    }
  ]
}`}
            </div>
            <div className="mt-2"><span className="text-slate-500">Body — Licences:</span></div>
            <div className="ml-4 whitespace-pre-wrap text-slate-200">
{`{ "rows": [ { "Employee Email": "kathy.abdoo@gbelectrical.com.au", "Licence Type": "Electrical Licence",
    "Licence Number": "EC-12345", "Issue Date": "2021-01-15",
    "Expiry Date": "2027-01-15", "Issuing Authority": "NSW Fair Trading" } ] }`}
            </div>
            <div className="mt-2"><span className="text-slate-500">Body — Certifications:</span></div>
            <div className="ml-4 whitespace-pre-wrap text-slate-200">
{`{ "rows": [ { "Employee Email": "kathy.abdoo@gbelectrical.com.au", "Certification Name": "Working at Heights",
    "Issuer": "SafetyFirst Training", "Issue Date": "2024-03-10",
    "Expiry Date": "2026-03-10" } ] }`}
            </div>
            <div className="text-slate-400 text-[10px] mt-3 leading-snug font-sans">
              In Power Automate, build the body with a <code className="text-emerald-400 bg-slate-800 px-1 rounded">Select</code> action
              (From: <code className="text-emerald-400 bg-slate-800 px-1 rounded">value</code> of Get items; Map: the fields above)
              then HTTP body <code className="text-emerald-400 bg-slate-800 px-1 rounded">{`{ "rows": @{body('Select')} }`}</code>.
              Dates must be YYYY-MM-DD. Exact column specs + CSV templates: <b>Recipes</b> tab.
            </div>
          </div>
        </TabsContent>

        <TabsContent value="health" className="mt-4">
          <FeedHealthPanel data={feedHealth} />
          <WritebackHealthPanel />
        </TabsContent>
      </Tabs>

      {/* Create-key dialog */}
      {newKeyDlg && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={(e) => { if (e.target === e.currentTarget) setNewKeyDlg(false); }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
            <div className="font-display text-lg font-semibold text-slate-900 mb-1">Create service key</div>
            <div className="text-[11px] text-slate-500 mb-4">The key secret is shown ONCE. Copy it into your Power Automate connection immediately.</div>
            <label className="text-xs text-slate-600 mb-1 block">Descriptive name</label>
            <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)}
                   placeholder="e.g. Power Automate — Monthly HR sync"
                   data-testid="import-key-name-input" className="h-9" />
            <div className="mt-2 text-[10px] text-slate-400">Scope: all recipes (least-privilege coming later — for now one key covers everything).</div>
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
              <Button size="sm" variant="outline" onClick={() => { setNewKeyDlg(false); setNewKeyName(""); }}>Cancel</Button>
              <Button size="sm" onClick={createKey} disabled={creatingKey}
                      data-testid="import-key-create-submit"
                      className="bg-gbe hover:bg-gbe/90 text-white ml-auto">
                {creatingKey ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1.5" />}
                Create
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Issued-secret modal — shown ONCE */}
      {issuedSecret && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-5">
            <div className="font-display text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Service key created
            </div>
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 my-3">
              <b>Copy this secret now.</b> It will never be shown again. Store it in your Power Automate connection or a password manager.
            </div>
            <label className="text-xs text-slate-600 mb-1 block">Full key (paste into <code className="text-[10px] bg-slate-100 px-1 rounded">X-GBE-Service-Key</code>)</label>
            <div className="flex items-center gap-2">
              <Input readOnly value={issuedSecret.secret}
                     data-testid="import-key-issued-secret"
                     className="font-mono text-[11px] h-9 bg-slate-50" />
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(issuedSecret.secret)}
                      data-testid="import-key-copy-btn">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mt-3">
              <div><span className="text-slate-400">Key ID:</span> <span className="font-mono">{issuedSecret.key_id}</span></div>
              <div><span className="text-slate-400">Name:</span> {issuedSecret.name}</div>
            </div>
            <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-100">
              <Button size="sm" onClick={() => setIssuedSecret(null)}
                      data-testid="import-key-issued-close"
                      className="bg-gbe hover:bg-gbe/90 text-white">
                I've saved the key — dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Recipe detail + upload sheet */}
      <Sheet open={!!openRecipe} onOpenChange={(o) => { if (!o) { setOpenRecipe(null); setPreview(null); setPreviewFile(null); } }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{detail?.title || "Loading…"}</SheetTitle>
            <SheetDescription>{detail?.description}</SheetDescription>
          </SheetHeader>

          {detail && (
            <div className="mt-4 space-y-4">
              {/* Column spec */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Column spec ({detail.columns?.length} columns)</div>
                  <Button size="sm" variant="outline" className="text-xs h-7"
                          onClick={() => downloadTemplate(openRecipe)}
                          data-testid={`import-detail-template-btn-${openRecipe}`}>
                    <FileDown className="w-3 h-3 mr-1.5" /> Download template
                  </Button>
                </div>
                <div className="overflow-x-auto max-h-[240px]">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase text-slate-400 border-b border-slate-100">
                        <th className="text-left font-medium px-3 py-1.5">CSV Header</th>
                        <th className="text-left font-medium px-3 py-1.5">Type</th>
                        <th className="text-left font-medium px-3 py-1.5">Required</th>
                        <th className="text-left font-medium px-3 py-1.5">Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.columns || []).map((c) => (
                        <tr key={c.source} className="border-b border-slate-50">
                          <td className="px-3 py-1.5 font-medium text-slate-800">{c.source}</td>
                          <td className="px-3 py-1.5 text-slate-500">
                            {c.dtype}
                            {c.dtype === "lookup" && (<span className="text-slate-400"> → {c.lookup_collection}.{c.lookup_field}</span>)}
                          </td>
                          <td className="px-3 py-1.5">
                            {c.required
                              ? <Badge className="text-[9px] bg-rose-100 text-rose-700 border border-rose-200">required</Badge>
                              : <span className="text-slate-400 text-[10px]">—</span>}
                          </td>
                          <td className="px-3 py-1.5 text-slate-500 truncate max-w-[220px]" title={String(c.example)}>{String(c.example)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Upload */}
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Upload file (CSV or XLSX)</div>
                <Input type="file" accept=".csv,.xlsx"
                       data-testid={`import-upload-file-${openRecipe}`}
                       onChange={(e) => handlePreview(e.target.files?.[0])}
                       disabled={uploading || applying} />
                {uploading && <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Parsing &amp; validating…</div>}
              </div>

              {/* Preview */}
              {preview && (
                <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
                  {preview.raw_workbook && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="import-ccew-workbook-summary">
                      <div className="flex items-center gap-2 text-rose-900 font-semibold text-sm mb-1.5">
                        <ShieldCheck className="w-4 h-4" />
                        BU {preview.bu} · {preview.period_month} — JSA &amp; CCEW audit workbook
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div><span className="text-slate-500">Jobs completed:</span> <span className="font-semibold tabular-nums">{fmtCell(preview.workbook_summary?.jobs_completed)}</span></div>
                        <div><span className="text-slate-500">Audits required:</span> <span className="font-semibold tabular-nums">{fmtCell(preview.workbook_summary?.audits_required)}</span></div>
                        <div><span className="text-slate-500">JSA completed:</span> <span className="font-semibold tabular-nums">{fmtCell(preview.workbook_summary?.jsa_completed)}</span></div>
                        <div><span className="text-slate-500">JSA failures:</span> <span className="font-semibold tabular-nums">{fmtCell(preview.workbook_summary?.jsa_failures)}</span></div>
                      </div>
                      <div className="text-[10px] text-rose-700 mt-1.5">Feeds the BU Performance <b>JSA Audit Completion</b> safety KPI for {preview.period_month}.</div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    <PreviewStat label="Total" value={preview.rows_total} tone="default" />
                    <PreviewStat label="Valid" value={preview.rows_valid} tone="good" />
                    <PreviewStat label="Would insert" value={preview.would_insert} tone="good" />
                    <PreviewStat label="Would update" value={preview.would_update} tone="info" />
                    <PreviewStat label="Errored" value={preview.rows_errored} tone={preview.rows_errored > 0 ? "danger" : "default"} />
                  </div>
                  {/* Error rows first */}
                  <div className="max-h-[300px] overflow-y-auto border border-slate-100 rounded">
                    <table className="w-full text-[10px]">
                      <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-2 py-1">Row #</th>
                          <th className="text-left px-2 py-1">Status</th>
                          <th className="text-left px-2 py-1">Issues / Preview</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...preview.preview].sort((a, b) => (b.errors.length > 0) - (a.errors.length > 0)).map((row) => (
                          <tr key={row.row_index} className={`border-b border-slate-50 ${row.errors.length > 0 ? "bg-rose-50/40" : ""}`}>
                            <td className="px-2 py-1 tabular-nums">{row.row_index}</td>
                            <td className="px-2 py-1">
                              {row.errors.length > 0
                                ? <span className="inline-flex items-center gap-1 text-rose-700"><XCircle className="w-3 h-3" />Error</span>
                                : <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" />OK</span>}
                            </td>
                            <td className="px-2 py-1 text-slate-600">
                              {row.errors.length > 0
                                ? <span className="text-rose-700">{row.errors.join(" · ")}</span>
                                : <span className="truncate block max-w-[380px]" title={JSON.stringify(row.doc)}>
                                    {Object.entries(row.doc).slice(0, 4).map(([k, v]) => `${k}=${v}`).join(", ")}
                                  </span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {preview.truncated && (
                    <div className="text-[10px] text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Preview truncated to first 200 rows — full file will still be processed on Apply.
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    <Button size="sm" variant="outline" onClick={() => { setPreview(null); setPreviewFile(null); }}
                            data-testid={`import-preview-cancel-${openRecipe}`}>Cancel</Button>
                    <Button size="sm" onClick={handleApply} disabled={applying || preview.rows_valid === 0}
                            data-testid={`import-preview-apply-${openRecipe}`}
                            className="bg-gbe hover:bg-gbe/90 text-white ml-auto">
                      {applying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5 mr-1.5" />}
                      {applying && applyProgress
                        ? `Applying part ${applyProgress}…`
                        : chunks && chunks.length > 1
                          ? `Apply all ${chunks.length} parts`
                          : <>Apply {preview.rows_valid} valid row{preview.rows_valid === 1 ? "" : "s"}</>}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Run detail */}
      <Sheet open={!!runDetail} onOpenChange={(o) => { if (!o) setRunDetail(null); }}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{runDetail?.recipe_title}</SheetTitle>
            <SheetDescription>{fmtWhen(runDetail?.uploaded_at)} by {runDetail?.uploaded_by_email}</SheetDescription>
          </SheetHeader>
          {runDetail && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <PreviewStat label="Total"     value={runDetail.rows_total}    tone="default" />
                <PreviewStat label="Inserted"  value={runDetail.rows_inserted} tone="good" />
                <PreviewStat label="Updated"   value={runDetail.rows_updated}  tone="info" />
                <PreviewStat label="Errored"   value={runDetail.rows_errored}
                             tone={runDetail.rows_errored > 0 ? "danger" : "default"} />
              </div>
              <div className="text-[11px] text-slate-500">Duration: {fmtNum(runDetail.duration_ms)} ms · File: {runDetail.filename}</div>
              {(runDetail.errors || []).length > 0 && (
                <div className="border border-rose-200 bg-rose-50/40 rounded p-3">
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold mb-2">Errors ({runDetail.errors.length})</div>
                  <ul className="space-y-1 text-[11px] text-rose-800 max-h-[400px] overflow-y-auto">
                    {runDetail.errors.map((e, i) => (
                      <li key={i}>Row {e.row_index}: {e.errors.join(" · ")}</li>
                    ))}
                  </ul>
                  {runDetail.errors_truncated && (
                    <div className="text-[10px] text-rose-600 mt-2">Errors truncated (500+ errors — inspect the file directly).</div>
                  )}
                </div>
              )}

              {/* Actual rows inserted/updated, with their column data */}
              <div className="border border-slate-200 rounded">
                <div className="flex flex-wrap items-center gap-2 p-2 border-b border-slate-100 bg-slate-50/60">
                  <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold mr-1" data-testid="run-rows-title">
                    Rows{runRows ? ` (${fmtNum(runRows.total)})` : ""}
                  </span>
                  {[
                    { k: "", label: `All ${runRows ? fmtNum((runRows.counts?.inserted || 0) + (runRows.counts?.updated || 0)) : ""}` },
                    { k: "inserted", label: `Inserted ${runRows ? fmtNum(runRows.counts?.inserted || 0) : ""}` },
                    { k: "updated", label: `Updated ${runRows ? fmtNum(runRows.counts?.updated || 0) : ""}` },
                  ].map((o) => (
                    <button key={o.k || "all"} data-testid={`run-rows-filter-${o.k || "all"}`}
                      onClick={() => { setRowOp(o.k); setRowPage(0); fetchRunRows(runDetail.id, { op: o.k, q: rowQ, page: 0 }); }}
                      className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${rowOp === o.k ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                      {o.label}
                    </button>
                  ))}
                  <div className="ml-auto flex items-center gap-1">
                    <Input data-testid="run-rows-search" value={rowQ}
                      onChange={(e) => setRowQ(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { setRowPage(0); fetchRunRows(runDetail.id, { op: rowOp, q: rowQ, page: 0 }); } }}
                      placeholder="Search rows…" className="h-7 w-40 text-xs" />
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid="run-rows-search-btn"
                      onClick={() => { setRowPage(0); fetchRunRows(runDetail.id, { op: rowOp, q: rowQ, page: 0 }); }}>
                      <Filter className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                {rowsLoading ? (
                  <div className="p-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
                ) : !runRows || runRows.rows.length === 0 ? (
                  <div className="p-6 text-center text-[11px] text-slate-400" data-testid="run-rows-empty">
                    No captured rows for this view.
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto max-h-[460px]">
                      <table className="w-full text-[11px]" data-testid="run-rows-table">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="text-left text-slate-500">
                            <th className="px-2 py-1.5 font-medium">#</th>
                            <th className="px-2 py-1.5 font-medium">Op</th>
                            {rowColumns.map((c) => (
                              <th key={c} className="px-2 py-1.5 font-medium whitespace-nowrap">{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {runRows.rows.map((r, i) => (
                            <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/60">
                              <td className="px-2 py-1.5 tabular-nums text-slate-400">{r.row_index}</td>
                              <td className="px-2 py-1.5">
                                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${r.op === "inserted" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                                  {r.op}
                                </span>
                              </td>
                              {rowColumns.map((c) => (
                                <td key={c} className="px-2 py-1.5 whitespace-nowrap max-w-[220px] truncate" title={fmtCell(r.doc[c])}>
                                  {fmtCell(r.doc[c])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center justify-between p-2 border-t border-slate-100 text-[11px] text-slate-500">
                      <span>
                        {runRows.skip + 1}–{Math.min(runRows.skip + runRows.rows.length, runRows.total)} of {fmtNum(runRows.total)}
                        {runDetail.rows_captured_truncated && <span className="text-amber-600"> · first {fmtNum(runDetail.rows_captured)} captured</span>}
                      </span>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" data-testid="run-rows-prev"
                          disabled={rowPage === 0}
                          onClick={() => { const p = rowPage - 1; setRowPage(p); fetchRunRows(runDetail.id, { op: rowOp, q: rowQ, page: p }); }}>
                          Prev
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" data-testid="run-rows-next"
                          disabled={(rowPage + 1) * ROW_PAGE >= runRows.total}
                          onClick={() => { const p = rowPage + 1; setRowPage(p); fetchRunRows(runDetail.id, { op: rowOp, q: rowQ, page: p }); }}>
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}


function PreviewStat({ label, value, tone = "default" }) {
  const cls = tone === "good"   ? "border-emerald-200 bg-emerald-50/40 text-emerald-700"
           : tone === "info"    ? "border-sky-200 bg-sky-50/40 text-sky-700"
           : tone === "danger"  ? "border-rose-200 bg-rose-50/40 text-rose-700"
           : "border-slate-200 bg-slate-50/40 text-slate-700";
  return (
    <div className={`rounded border ${cls} px-2 py-1.5`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-display font-semibold tabular-nums">{fmtNum(value)}</div>
    </div>
  );
}


// =====================================================================
// ComplianceRawDiagnosticCard — proves whether SharePoint/Power Automate
// actually sends a document name ("Name" column) per compliance row, or
// whether it's lost before the Hub receives it. Reads the raw payload
// captured on the LAST compliance import.
// =====================================================================
function ComplianceRawDiagnosticCard({ active }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (onlyMissing) params.set("only_missing", "true");
      const { data } = await api.get(`/imports/diagnostics/compliance-raw?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error("Could not load the document diagnostic");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (active) load(); /* eslint-disable-next-line */ }, [active]);

  const nameKeyPresent = data?.header_keys?.some((k) => k.toLowerCase() === "name");

  return (
    <div className="space-y-4" data-testid="compliance-doc-diagnostic">
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
              <FileSearch className="w-4 h-4 text-slate-500" /> Compliance document-name check
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Shows the <span className="font-medium">raw data</span> captured from your last Compliance Register import
              (SharePoint / Power Automate). It proves whether each row actually arrived with a document name
              (<code className="text-[11px]">Name</code> column) — so we can tell if a missing document is lost
              <span className="font-medium"> at source</span> or <span className="font-medium">inside the hub</span>.
            </p>
          </div>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load} disabled={loading}
                  data-testid="doc-diagnostic-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Refresh
          </Button>
        </div>

        {!loading && data && !data.captured && (
          <div className="mt-4 rounded border border-amber-200 bg-amber-50/60 px-3 py-3 text-xs text-amber-800"
               data-testid="doc-diagnostic-empty">
            No compliance import has been captured yet. Run your next SharePoint / Power Automate
            Compliance Register import, then click Refresh — the raw document data will appear here.
          </div>
        )}

        {data?.captured && (
          <>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <PreviewStat label="Rows captured" value={data.row_count} />
              <PreviewStat label="With a document name" value={data.name_present_count} tone="good" />
              <PreviewStat label="Missing a name" value={data.name_missing_count}
                           tone={data.name_missing_count > 0 ? "danger" : "default"} />
              <div className="rounded border border-slate-200 bg-slate-50/40 px-2 py-1.5">
                <div className="text-[9px] uppercase tracking-wider opacity-70 text-slate-700">Captured</div>
                <div className="text-xs font-medium text-slate-700 mt-1">{fmtWhen(data.captured_at)}</div>
                <div className="text-[10px] text-slate-500">{data.source}</div>
              </div>
            </div>

            <div className="mt-3 text-xs">
              <span className="text-slate-500">The import sent a </span>
              <code className="text-[11px] px-1 rounded bg-slate-100">Name</code>
              <span className="text-slate-500"> column: </span>
              {nameKeyPresent
                ? <span className="font-medium text-emerald-700">Yes</span>
                : <span className="font-medium text-rose-700">No — the document column never arrived</span>}
            </div>

            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                All columns received ({data.header_keys?.length || 0})
              </summary>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(data.header_keys || []).map((k) => (
                  <Badge key={k} variant="outline"
                         className={`text-[10px] ${k.toLowerCase() === "name" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : ""}`}>
                    {k}
                  </Badge>
                ))}
              </div>
            </details>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && load()}
                     placeholder="Search a person, client or filename (e.g. Oscar Stein)"
                     className="h-8 text-xs max-w-xs" data-testid="doc-diagnostic-search" />
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load}
                      data-testid="doc-diagnostic-search-btn">Search</Button>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={onlyMissing}
                       onChange={(e) => { setOnlyMissing(e.target.checked); }}
                       data-testid="doc-diagnostic-only-missing" />
                Only rows missing a name
              </label>
            </div>

            <div className="mt-3 border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left px-3 py-2 font-medium">Employee</th>
                    <th className="text-left px-3 py-2 font-medium">Type</th>
                    <th className="text-left px-3 py-2 font-medium">Client</th>
                    <th className="text-left px-3 py-2 font-medium">Name column</th>
                    <th className="text-left px-3 py-2 font-medium">Document name received</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.samples || []).map((s, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60"
                        data-testid={`doc-diagnostic-row-${i}`}>
                      <td className="px-3 py-2 font-medium text-slate-900">{s.employee || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2 text-slate-600">{s.content_type || s.item || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2 text-slate-600">{s.client || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2">
                        {s.name_key_present
                          ? <span className="text-emerald-700">present</span>
                          : <span className="text-rose-700 font-medium">absent</span>}
                      </td>
                      <td className="px-3 py-2">
                        {s.name_value
                          ? <span className="text-slate-800">{s.name_value}</span>
                          : <span className="text-amber-600 font-medium">
                              {s.name_key_present ? "— blank —" : "— column not sent —"}
                            </span>}
                      </td>
                    </tr>
                  ))}
                  {(data.samples || []).length === 0 && (
                    <tr><td colSpan="5" className="px-3 py-8 text-center text-slate-500">No rows match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {data.total > (data.samples?.length || 0) && (
              <p className="mt-2 text-[11px] text-slate-400">Showing {data.samples.length} of {data.total} matching rows.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// =====================================================================
// EmployeeDedupeCard — admin utility to clean up duplicate Employees
// rows (produced when Power Automate re-imported without employee_number).
// Two-step: Preview (read-only) → Apply (destructive but audit-logged).
// =====================================================================
function EmployeeDedupeCard() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null);

  const runPreview = async () => {
    setBusy(true); setApplied(null);
    try {
      const { data } = await api.get("/imports/dedupe/employees/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const runApply = async () => {
    if (!window.confirm(
      `About to permanently delete ${preview?.employees_to_delete ?? 0} duplicate employee rows.\n\n` +
      "Linked HR licences / certifications / QMS records will be re-parented to the kept _id first.\n\n" +
      "Have you taken a Mongo snapshot?"
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/imports/dedupe/employees/apply");
      setApplied(data);
      toast.success(`Deleted ${data.employees_deleted} dupes · re-parented ${(data.linked_records_reparented?.hr_licences || 0) + (data.linked_records_reparented?.hr_certifications || 0) + (data.linked_records_reparented?.qms_kpi_values || 0)} linked records`);
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dedupe failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4"
         data-testid="employee-dedupe-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-amber-700" />
            Employee Dedupe (by Email)
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            Cleans up duplicate employees where <code>employee_number</code> was blank on a
            re-import. Kept doc = most-recently-updated per email. Linked HR licences,
            certifications and QMS refs are re-parented before delete.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={runPreview} disabled={busy}
                  data-testid="dedupe-preview-btn"
                  className="text-xs">
            {busy && !preview ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            1. Preview
          </Button>
          <Button size="sm" onClick={runApply}
                  disabled={busy || !preview || preview.employees_to_delete === 0}
                  data-testid="dedupe-apply-btn"
                  className="text-xs bg-rose-600 hover:bg-rose-700 text-white">
            {busy && preview ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            2. Apply (destructive)
          </Button>
        </div>
      </div>

      {preview && !applied && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="bg-white border rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Total email groups</div>
            <div className="font-display text-xl font-bold tabular-nums">{preview.total_email_groups?.toLocaleString?.() ?? preview.total_email_groups}</div>
          </div>
          <div className="bg-white border border-amber-200 rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-amber-700">Duplicate groups</div>
            <div className="font-display text-xl font-bold tabular-nums text-amber-800">{preview.duplicate_groups}</div>
          </div>
          <div className="bg-white border border-rose-200 rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-rose-700">Rows to delete</div>
            <div className="font-display text-xl font-bold tabular-nums text-rose-700">{preview.employees_to_delete}</div>
          </div>

          {preview.duplicate_groups > 0 && (
            <div className="col-span-3 bg-white border rounded p-3 max-h-64 overflow-y-auto">
              <div className="text-[11px] font-semibold text-slate-700 mb-2">Sample of duplicates (first 20):</div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[10px] uppercase text-slate-500 border-b">
                    <th className="text-left py-1 pr-2">Email</th>
                    <th className="text-left py-1 pr-2">Kept (Emp#)</th>
                    <th className="text-right py-1">To delete</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.groups.slice(0, 20).map((g) => (
                    <tr key={g.email} className="border-b border-slate-100">
                      <td className="py-1 pr-2 font-mono">{g.email}</td>
                      <td className="py-1 pr-2 text-slate-600">{g.kept.employee_number || <span className="text-slate-400 italic">null</span>} — {g.kept.first_name} {g.kept.last_name}</td>
                      <td className="py-1 text-right tabular-nums text-rose-700">{g.delete_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.duplicate_groups === 0 && (
            <div className="col-span-3 text-center text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded py-2">
              ✓ No duplicates — the Employees collection is clean.
            </div>
          )}
        </div>
      )}

      {applied && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900">
          <div className="font-semibold mb-1">✓ Dedupe complete</div>
          <ul className="space-y-0.5 ml-4 list-disc">
            <li>{applied.employees_deleted} duplicate employees deleted</li>
            <li>{applied.linked_records_reparented?.hr_licences ?? 0} HR licences re-parented</li>
            <li>{applied.linked_records_reparented?.hr_certifications ?? 0} HR certifications re-parented</li>
            <li>{applied.linked_records_reparented?.qms_kpi_values ?? 0} QMS KPI values re-parented</li>
            <li>{applied.groups_deduped} email groups deduped</li>
          </ul>
          <div className="mt-1 text-emerald-700">Audit row written to import_runs (source: admin_dedupe).</div>
        </div>
      )}
    </div>
  );
}


// =====================================================================
// EmployeeIdDedupeCard — collapse employees that share the same SharePoint
// list item ID (employee_number). That ID is globally unique, so same-ID
// rows are always the same person (e.g. the twin-Luke-Porter case where an
// email change spawned a second record). Keeps the ACTIVE / most-recently
// -updated record and re-parents linked HR + KPI data before deleting losers.
// =====================================================================
function EmployeeIdDedupeCard() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null);

  const runPreview = async () => {
    setBusy(true); setApplied(null);
    try {
      const { data } = await api.get("/imports/dedupe/employees/by-id/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const runApply = async () => {
    if (!window.confirm(
      `About to merge ${preview?.duplicate_id_groups ?? 0} duplicate-ID group(s) and permanently delete ${preview?.employees_to_delete ?? 0} record(s).\n\n` +
      "The active / most-recently-updated record is kept. Linked HR licences, certifications, inductions and QMS data are re-parented to it first.\n\n" +
      "Have you taken a Mongo snapshot?"
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/imports/dedupe/employees/by-id/apply");
      setApplied(data);
      toast.success(`Merged ${data.duplicate_id_groups} ID group(s) · deleted ${data.employees_deleted} duplicate(s)`);
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dedupe failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4"
         data-testid="employee-id-dedupe-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-indigo-700" />
            Fix Duplicate SharePoint IDs
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            Collapses employees that share the same SharePoint list item ID
            (<code>employee_number</code>) into one record — the result of an
            email change creating a twin. Kept record = active, most-recently
            -updated. Linked HR + QMS data are re-parented before delete.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={runPreview} disabled={busy}
                  data-testid="dedupe-byid-preview-btn"
                  className="text-xs">
            {busy && !preview ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            1. Preview
          </Button>
          <Button size="sm" onClick={runApply}
                  disabled={busy || !preview || preview.duplicate_id_groups === 0}
                  data-testid="dedupe-byid-apply-btn"
                  className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
            {busy && preview ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            2. Merge (destructive)
          </Button>
        </div>
      </div>

      {preview && !applied && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="bg-white border border-indigo-200 rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-indigo-700">Duplicate-ID groups</div>
            <div className="font-display text-xl font-bold tabular-nums text-indigo-800">{preview.duplicate_id_groups}</div>
          </div>
          <div className="bg-white border border-rose-200 rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-rose-700">Records to delete</div>
            <div className="font-display text-xl font-bold tabular-nums text-rose-700">{preview.employees_to_delete}</div>
          </div>

          {preview.duplicate_id_groups > 0 && (
            <div className="col-span-2 bg-white border rounded p-3 max-h-64 overflow-y-auto">
              <div className="text-[11px] font-semibold text-slate-700 mb-2">Groups (first 20):</div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[10px] uppercase text-slate-500 border-b">
                    <th className="text-left py-1 pr-2">Emp# (SharePoint ID)</th>
                    <th className="text-left py-1 pr-2">Kept</th>
                    <th className="text-left py-1">Deleted</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.groups.slice(0, 20).map((g) => (
                    <tr key={g.employee_number} className="border-b border-slate-100 align-top">
                      <td className="py-1 pr-2 font-mono">{g.employee_number}</td>
                      <td className="py-1 pr-2 text-emerald-700">{g.kept.name} <span className="text-slate-400">· {g.kept.email}</span>{g.kept.active ? "" : <span className="text-amber-600"> (inactive)</span>}</td>
                      <td className="py-1 text-rose-700">{g.deleted.map((d) => `${d.name} · ${d.email || "no email"}`).join("; ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.duplicate_id_groups === 0 && (
            <div className="col-span-2 text-center text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded py-2">
              ✓ No shared SharePoint IDs — every employee ID is unique.
            </div>
          )}
        </div>
      )}

      {applied && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900">
          <div className="font-semibold mb-1">✓ Duplicate-ID merge complete</div>
          <ul className="space-y-0.5 ml-4 list-disc">
            <li>{applied.duplicate_id_groups} ID group(s) merged</li>
            <li>{applied.employees_deleted} duplicate employee(s) deleted</li>
            <li>{applied.linked_records_reparented?.hr_licences ?? 0} HR licences re-parented</li>
            <li>{applied.linked_records_reparented?.hr_certifications ?? 0} HR certifications re-parented</li>
            <li>{applied.linked_records_reparented?.hr_inductions ?? 0} HR inductions re-parented</li>
            <li>{applied.linked_records_reparented?.qms_kpi_values ?? 0} QMS KPI values re-parented</li>
          </ul>
          <div className="mt-1 text-emerald-700">Audit row written to import_runs (source: admin_dedupe_by_id).</div>
        </div>
      )}
    </div>
  );
}


const FEED_STATUS_STYLE = {
  healthy:   { badge: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Healthy" },
  overdue:   { badge: "bg-rose-50 text-rose-700 border-rose-200", label: "Overdue" },
  never_ran: { badge: "bg-amber-50 text-amber-700 border-amber-200", label: "Never ran" },
  manual:    { badge: "bg-slate-100 text-slate-500 border-slate-200", label: "Manual" },
};
const FREQ_LABEL = { daily: "Daily", weekly: "Weekly", monthly: "Monthly", manual: "Manual" };

function WritebackHealthPanel() {
  const [data, setData] = useState(null);
  const [tagRun, setTagRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/field/writeback/failures").then(({ data }) => setData(data)).catch(() => {});
    api.get("/field/aroflo/push-tags/status").then(({ data }) => setTagRun(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tagRun?.status !== "running") return;
    const t = setInterval(() => {
      api.get("/field/aroflo/push-tags/status").then(({ data }) => setTagRun(data)).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [tagRun?.status]);
  const retry = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/field/writeback/${id}/retry`);
      toast[data.status === "ok" ? "success" : "warning"](`Retry: ${data.status}${data.reason ? ` — ${data.reason}` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
    finally { setBusy(false); }
  };
  const pushTags = async () => {
    if (!window.confirm("Push hub tag IDs into AroFlo's 'GB Asset ID Number' field AND the scannable GBEHub asset URL (gbehub.com.au/a/TAG) into each asset's Barcode field? Only mismatches are updated.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/field/aroflo/push-tags");
      toast[data.ok ? "success" : "warning"](data.ok ? "Tag push started — progress updates live below" : data.reason);
      setTimeout(load, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
    finally { setBusy(false); }
  };
  const st = tagRun?.stats || {};
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="writeback-health-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800">AroFlo write-back health</div>
          <div className="text-[11px] text-slate-500">
            {data ? (data.failed_7d ? `${data.failed_7d} failed push(es) in the last 7 days` : "All pushes OK in the last 7 days") : "…"}
            {tagRun && tagRun.status !== "never_run" && (
              <> · Tag/QR-link push <span className={tagRun.status === "failed" ? "text-rose-600 font-medium" : tagRun.status === "running" ? "text-amber-600 font-medium" : "text-emerald-700 font-medium"}>{tagRun.status}</span>
                {tagRun.status === "running" && tagRun.phase ? <> — {tagRun.phase}{st.tags_minted ? ` · ${st.tags_minted} tags minted` : ""}</> : null}
                : {st.updated || 0} updated ({st.barcodes_set || 0} QR links, {st.tags_set || 0} tag IDs), {st.already_ok || 0} listed-matching, {st.barcode_skipped || 0} already linked{st.errors ? `, ${st.errors} errors` : ""}
                {tagRun.status === "failed" && tagRun.error ? <span className="text-rose-600"> — {tagRun.error}</span> : null}</>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="writeback-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={pushTags} disabled={busy || tagRun?.status === "running"}
                  data-testid="writeback-push-tags">
            Push tags + QR links → AroFlo
          </Button>
        </div>
      </div>
      {(data?.rows || []).length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
            <tr><th className="text-left py-1.5">When</th><th className="text-left">Asset</th>
                <th className="text-left">Fields</th><th className="text-left">Error</th><th></th></tr>
          </thead>
          <tbody>
            {data.rows.slice(0, 10).map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="py-1.5 text-slate-500 whitespace-nowrap">{(r.at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="font-mono text-[11px]">{r.tag_id || r.asset_id}</td>
                <td>{(r.fields || []).join(", ")}</td>
                <td className="text-rose-700 max-w-[280px] truncate">{r.retry_status === "ok" ? "retried OK" : (r.reason || (r.response || "").slice(0, 80))}</td>
                <td className="text-right">
                  {r.retry_status !== "ok" && (
                    <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={busy}
                            data-testid={`writeback-retry-${r.id}`} onClick={() => retry(r.id)}>Retry</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function FeedHealthPanel({ data }) {
  if (!data) {
    return <div className="text-sm text-slate-500 py-10 text-center" data-testid="feed-health-empty">Feed health unavailable — refresh to retry.</div>;
  }
  const { feeds = [], summary = {} } = data;
  return (
    <div data-testid="feed-health-panel">
      <div className="mb-3 text-xs text-slate-500">
        Tracks when each automated feed (Power Automate flows, email ingests, AroFlo syncs) last delivered data against its expected frequency.
        A feed is flagged <span className="font-medium text-rose-600">Overdue</span> when quiet beyond its grace window (daily: 2 days · weekly: 10 days · monthly: 40 days).
        Manual recipes are listed for reference and never flagged.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          ["healthy", "Healthy", "text-emerald-700"],
          ["overdue", "Overdue", "text-rose-700"],
          ["never_ran", "Never ran", "text-amber-700"],
          ["manual", "Manual", "text-slate-500"],
        ].map(([key, label, tone]) => (
          <div key={key} data-testid={`feed-health-stat-${key}`} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
            <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${tone}`}>{summary[key] ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              {["Feed", "Module", "Frequency", "Last run", "Last result", "Status"].map((c) => (
                <th key={c} className="text-left font-medium px-4 py-2.5">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {feeds.map((f) => {
              const st = FEED_STATUS_STYLE[f.status] || FEED_STATUS_STYLE.manual;
              return (
                <tr key={f.feed_id} data-testid={`feed-health-row-${f.feed_id}`} className="border-b border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{f.title}</div>
                    <div className="text-[11px] text-slate-400 font-mono">{f.feed_id}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 capitalize">{f.module}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {FREQ_LABEL[f.frequency] || f.frequency}
                    {f.threshold_days != null && <span className="text-[11px] text-slate-400"> · flag &gt;{f.threshold_days}d</span>}
                  </td>
                  <td className="px-4 py-3">
                    {f.last_run_at ? (
                      <div>
                        <div className="text-slate-700 tabular-nums text-[13px]">{fmtWhen(f.last_run_at)}</div>
                        <div className="text-[11px] text-slate-400">
                          {f.days_since != null ? `${f.days_since}d ago · ` : ""}{f.last_source || ""}
                        </div>
                      </div>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {f.last_rows_total != null ? (
                      <div className="text-[12px] text-slate-600 tabular-nums">
                        {fmtNum(f.last_rows_total)} rows
                        {(f.last_rows_errored || 0) > 0 && <span className="text-rose-600"> · {fmtNum(f.last_rows_errored)} err</span>}
                      </div>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${st.badge}`}>{st.label}</span>
                  </td>
                </tr>
              );
            })}
            {feeds.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500 text-sm">No feeds registered.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
